import { useState } from 'react'
import { ChevronRight, ChevronDown } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/utils/cn'
import {
  GROUPING_METHOD_BADGE_VARIANT,
  GROUPING_METHOD_LABEL,
} from '@/components/StoreModels/constants'
import type { GroupingMethod } from '@/types'

const METRIC_LABEL: Record<string, string> = {
  SALES_VOLUME: 'Sales Volume',
  UNITS: 'Units',
  GM_PERCENT: 'Gross Margin %',
}

interface GroupingConfigReadOnlyProps {
  method: GroupingMethod
  config: Record<string, unknown>
}

function ConfigField({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground">{children}</span>
    </div>
  )
}

function MethodConfig({
  method,
  config,
}: {
  method: GroupingMethod
  config: Record<string, unknown>
}) {
  switch (method) {
    case 'K_MEANS':
      return (
        <ConfigField label="Number of Clusters">
          {config.number_of_clusters != null
            ? String(config.number_of_clusters)
            : '—'}
        </ConfigField>
      )

    case 'PERCENTILE':
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ConfigField label="Metric">
            {config.metric
              ? (METRIC_LABEL[config.metric as string] ?? String(config.metric))
              : '—'}
          </ConfigField>
          <ConfigField label="Breakpoints">
            {Array.isArray(config.breakpoints)
              ? (config.breakpoints as number[]).join(', ')
              : '—'}
          </ConfigField>
        </div>
      )

    case 'CUMULATIVE_CONTRIBUTION':
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ConfigField label="Metric">
            {config.metric
              ? (METRIC_LABEL[config.metric as string] ?? String(config.metric))
              : '—'}
          </ConfigField>
          <ConfigField label="Thresholds">
            {Array.isArray(config.thresholds)
              ? (config.thresholds as number[]).join(', ')
              : '—'}
          </ConfigField>
        </div>
      )

    case 'ATTRIBUTE':
      return (
        <ConfigField label="Attribute Name">
          {config.attribute_name ? String(config.attribute_name) : '—'}
        </ConfigField>
      )

    case 'MANUAL':
      return (
        <ConfigField label="Groups">
          {config.groups ? (
            <pre className="mt-1 text-xs font-mono text-muted-foreground whitespace-pre-wrap bg-muted/50 rounded-md p-3">
              {JSON.stringify(config.groups, null, 2)}
            </pre>
          ) : (
            '—'
          )}
        </ConfigField>
      )

    case 'POG_VERSION':
      return (
        <ConfigField label="POG Group">
          {config.pog_group ? String(config.pog_group) : '—'}
        </ConfigField>
      )

    default:
      return null
  }
}

export function GroupingConfigReadOnly({
  method,
  config,
}: GroupingConfigReadOnlyProps) {
  const [open, setOpen] = useState(false)

  return (
    <div className="border border-border rounded-lg">
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className="flex w-full items-center gap-2 px-4 py-3 text-sm font-medium text-foreground hover:bg-muted/50 transition-colors cursor-pointer"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0" />
        )}
        Grouping Configuration
      </button>
      <div
        className={cn(
          'overflow-hidden transition-[max-height] duration-200 ease-in-out',
          open ? 'max-h-[10000px]' : 'max-h-0'
        )}
      >
        <div className="px-4 pb-4 flex flex-col gap-4">
          <ConfigField label="Method">
            <Badge variant={GROUPING_METHOD_BADGE_VARIANT[method]}>
              {GROUPING_METHOD_LABEL[method]}
            </Badge>
          </ConfigField>
          <MethodConfig method={method} config={config} />
        </div>
      </div>
    </div>
  )
}
